import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    // Color swatches in preview
    'primarySwatch',
    'secondarySwatch',
    'accentSwatch',
    // Typography preview
    'previewBackground',
    'previewHeading',
    'previewBody',
    // Button preview
    'previewButton',
    // UI library info panel
    'libraryName',
    'libraryDescription',
    'libraryCdn'
  ]

  static values = {
    // UI library metadata passed from server (WebsiteStyleGuide::UI_LIBRARIES)
    // This eliminates JS/Ruby drift risk
    libraries: Object
  }

  // Track which fonts have been loaded
  loadedFonts = new Set()

  // Fallback library info if server data not available
  static defaultLibraryInfo = {
    'none': {
      name: 'Plain Tailwind',
      description: 'Default Tailwind CSS styling without additional component libraries.',
      cdn: null
    }
  }

  // Get library info from server-provided value or fallback
  get libraryInfo() {
    return this.hasLibrariesValue ? this.librariesValue : this.constructor.defaultLibraryInfo
  }

  connect() {
    // Set up color field sync
    this.setupColorSync()
    // Load initial fonts and update preview
    this.loadCurrentFonts()
    this.updatePreview()
  }

  // Load the currently selected fonts
  loadCurrentFonts() {
    const headingFont = this.getSelectValue('heading_font')
    const bodyFont = this.getSelectValue('body_font')

    if (headingFont) this.loadGoogleFont(headingFont)
    if (bodyFont) this.loadGoogleFont(bodyFont)
  }

  // Dynamically load a Google Font
  loadGoogleFont(fontName) {
    if (!fontName || this.loadedFonts.has(fontName)) return

    // Mark as loading to prevent duplicate loads
    this.loadedFonts.add(fontName)

    // Create link element for Google Fonts
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`

    // Append to head
    document.head.appendChild(link)
  }

  setupColorSync() {
    // Find all color input pairs and sync them
    const colorFields = [
      'primary_color',
      'secondary_color',
      'accent_color',
      'background_color',
      'text_color',
      'heading_color'
    ]

    colorFields.forEach((field) => {
      // Color picker uses data-color-for attribute, text input uses name
      const colorInput = this.element.querySelector(`input[type="color"][data-color-for="${field}"]`)
      const textInput = this.element.querySelector(`input[type="text"][name*="${field}"]`)

      if (colorInput && textInput) {
        // Sync color picker -> text input
        colorInput.addEventListener('input', (e) => {
          textInput.value = e.target.value.toUpperCase()
          this.updatePreview()
        })

        // Sync text input -> color picker
        textInput.addEventListener('input', (e) => {
          const value = e.target.value
          // Only update if it's a valid hex color
          if (/^#[0-9A-Fa-f]{6}$/i.test(value)) {
            colorInput.value = value
            this.updatePreview()
          }
        })

        // Also handle change events for final value
        textInput.addEventListener('change', (e) => {
          let value = e.target.value.trim()
          // Auto-add # if missing
          if (/^[0-9A-Fa-f]{6}$/.test(value)) {
            value = `#${value}`
            e.target.value = value
          }
          if (/^#[0-9A-Fa-f]{6}$/i.test(value)) {
            colorInput.value = value
            e.target.value = value.toUpperCase()
            this.updatePreview()
          }
        })
      }
    })
  }

  // Called when any form field changes
  updatePreview() {
    this.updateColorSwatches()
    this.updateTypographyPreview()
    this.updateButtonPreview()
  }

  updateColorSwatches() {
    const primary = this.getColorValue('primary_color')
    const secondary = this.getColorValue('secondary_color')
    const accent = this.getColorValue('accent_color')

    if (this.hasPrimarySwatchTarget) {
      this.primarySwatchTarget.style.backgroundColor = primary
    }
    if (this.hasSecondarySwatchTarget) {
      this.secondarySwatchTarget.style.backgroundColor = secondary
    }
    if (this.hasAccentSwatchTarget) {
      this.accentSwatchTarget.style.backgroundColor = accent
    }
  }

  updateTypographyPreview() {
    const background = this.getColorValue('background_color')
    const headingColor = this.getColorValue('heading_color')
    const textColor = this.getColorValue('text_color')
    const headingFont = this.getSelectValue('heading_font')
    const bodyFont = this.getSelectValue('body_font')
    const fontSize = this.getSelectValue('font_size_base')

    // Load fonts if not already loaded
    if (headingFont) this.loadGoogleFont(headingFont)
    if (bodyFont) this.loadGoogleFont(bodyFont)

    if (this.hasPreviewBackgroundTarget) {
      this.previewBackgroundTarget.style.backgroundColor = background
    }
    if (this.hasPreviewHeadingTarget) {
      this.previewHeadingTarget.style.color = headingColor
      this.previewHeadingTarget.style.fontFamily = `'${headingFont}', sans-serif`
    }
    if (this.hasPreviewBodyTarget) {
      this.previewBodyTarget.style.color = textColor
      this.previewBodyTarget.style.fontFamily = `'${bodyFont}', sans-serif`
      this.previewBodyTarget.style.fontSize = fontSize
    }
  }

  updateButtonPreview() {
    const primary = this.getColorValue('primary_color')
    const bodyFont = this.getSelectValue('body_font')
    const buttonStyle = this.getSelectValue('button_style')

    // Load font if not already loaded
    if (bodyFont) this.loadGoogleFont(bodyFont)

    let borderRadius = '0.375rem'
    switch (buttonStyle) {
      case 'pill': borderRadius = '9999px'; break
      case 'square': borderRadius = '0'; break
      case 'rounded': borderRadius = '0.375rem'; break
    }

    if (this.hasPreviewButtonTarget) {
      this.previewButtonTarget.style.backgroundColor = primary
      this.previewButtonTarget.style.fontFamily = `'${bodyFont}', sans-serif`
      this.previewButtonTarget.style.borderRadius = borderRadius
    }
  }

  getColorValue(fieldName) {
    // Prefer the text input value since it's what will be submitted
    const textInput = this.element.querySelector(`input[type="text"][name*="${fieldName}"]`)
    if (textInput && /^#[0-9A-Fa-f]{6}$/.test(textInput.value)) {
      return textInput.value
    }
    const colorInput = this.element.querySelector(`input[type="color"][name*="${fieldName}"]`)
    return colorInput?.value || '#000000'
  }

  getSelectValue(fieldName) {
    const select = this.element.querySelector(`select[name*="${fieldName}"]`)
    return select?.value || ''
  }

  // Action handlers for select changes
  onSelectChange() {
    this.updatePreview()
  }

  // Called when UI library dropdown changes
  onLibraryChange(event) {
    const library = event.target.value
    const info = this.libraryInfo[library]

    if (!info) return

    // Update the info panel targets if they exist
    if (this.hasLibraryNameTarget) {
      this.libraryNameTarget.textContent = info.name
    }
    if (this.hasLibraryDescriptionTarget) {
      this.libraryDescriptionTarget.textContent = info.description
    }
    if (this.hasLibraryCdnTarget) {
      if (info.cdn) {
        this.libraryCdnTarget.textContent = info.cdn
        this.libraryCdnTarget.closest('.cdn-info')?.classList.remove('hidden')
      } else {
        this.libraryCdnTarget.textContent = 'None (uses Tailwind CDN only)'
        this.libraryCdnTarget.closest('.cdn-info')?.classList.add('hidden')
      }
    }

    // Update preview (library change may affect button styles, etc.)
    this.updatePreview()
  }
}
